import { useState } from 'react';
import type { LibraryAction, MachineListRow } from '@nightshift/ui-types';
import { api } from '../live/api.ts';
import { relativeTime } from '../lib/format.ts';

/** `RETIRED_COPY_RETENTION_MS` in core's `runner-libraries.ts`; the dashboard cannot import core. */
const RETIRED_COPY_DAYS = 14;
/** `libraryProjects` keys a folder no registered project claims this way. */
const UNREGISTERED = 'unregistered:';

export function MachineLibraries({ row, canWrite }: { row: MachineListRow; canWrite: boolean }) {
  const [folder, setFolder] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const [destination, setDestination] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const report = row.libraries?.report;
  const pending = busy || !!row.libraries?.command;
  if (!report) return <p className="cfg-help">Libraries have not been reported by this runner.</p>;
  const send = async (action: LibraryAction) => {
    setBusy(true);
    setError('');
    try {
      await api.setMachineLibraries(row.id, action);
      setRemoving(null);
      setFolder('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };
  const libraries = [...report.libraries].sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
  const projects = row.libraries?.projects ?? [];
  const retired = [...(report.retired ?? [])].sort((a, b) => a.retiredAt - b.retiredAt);
  return <section className="machine-libraries" aria-label="Runner libraries">
    <h3 className="machine-card-legend">Libraries</h3>
    <p className="cfg-help">Storage folders on this machine. Paths show the physical location when reported by the runner. New projects use the first available enabled library; existing work stays in its current location.</p>
    {pending ? <p role="status">Waiting for runner acknowledgement…</p> : null}
    {error || report.receipt?.error ? <p role="alert">{error || report.receipt?.error}</p> : null}
    <ul className="library-list">{libraries.map(library => <li key={library.id}>
      <code>{library.physicalPath ?? library.path}</code>
      <p>Priority {library.priority} · {library.enabled ? 'Enabled' : 'Disabled'} · {library.available ? 'Available' : 'Unavailable'}</p>
      {canWrite ? <div className="library-actions">
        <button className="btn btn-sm" disabled={pending} onClick={() => void send({ kind: 'configure', libraryId: library.id, enabled: !library.enabled, priority: library.priority })}>{library.enabled ? 'Disable' : 'Enable'}</button>
        <button className="btn btn-sm" disabled={pending} onClick={() => void send({ kind: 'configure', libraryId: library.id, enabled: true, priority: 0 })}>Use for new work</button>
        <button className="btn btn-sm" disabled={pending || libraries.length === 1} title={libraries.length === 1 ? 'The final library cannot be deleted' : undefined} onClick={() => { setRemoving(library.id); setDestination(''); setConfirmed(false); }}>Remove library</button>
      </div> : null}
      {removing === library.id ? <fieldset disabled={pending}>
        <legend>Remove library</legend>
        <p>{projects.filter(project => project.libraryIds.includes(library.id)).length} stored projects. Stop running work before removal.</p>
        <label>Stored work <select aria-label="Stored work" value={destination} onChange={event => { setDestination(event.target.value); setConfirmed(false); }}>
          <option value="">Choose what happens to stored work</option>
          {libraries.filter(other => other.id !== library.id && other.enabled).map(other => <option key={other.id} value={other.id}>Migrate to {other.physicalPath ?? other.path}</option>)}
          <option value="delete">Delete stored work</option>
        </select></label>
        {destination === 'delete' ? <label><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} /> I confirm deletion of all work in this library.</label> : <p className="cfg-help">Migration keeps the old copies for 14 days for recovery; future work uses the destination.</p>}
        <button className="btn btn-sm" disabled={!destination || (destination === 'delete' && !confirmed)} onClick={() => void send({ kind: 'remove', libraryId: library.id, ...(destination === 'delete' ? { confirmedDelete: true } : { destinationId: destination }) })}>Confirm removal</button>
        <button className="btn btn-sm" onClick={() => setRemoving(null)}>Cancel</button>
      </fieldset> : null}
    </li>)}</ul>
    {canWrite ? <form onSubmit={event => { event.preventDefault(); void send({ kind: 'add', path: folder.trim() }); }}>
      <label>Library path <input aria-label="Library path" value={folder} onChange={event => setFolder(event.target.value)} disabled={pending} /></label>
      <button className="btn btn-sm" disabled={pending || !folder.trim()}>Add library</button>
    </form> : null}
    <details><summary>Stored projects ({projects.length})</summary>
      {projects.map(project => <section className="library-project" key={project.key} aria-label={`${project.name} storage`}>
        <h4>{project.name}</h4>
        <dl className="library-project-paths">{([
          ['Main checkout', project.mainDirectories],
          ['Sidecar work', project.sidecarDirectories],
          ['Worktrees', project.worktreeDirectories],
        ] as const).map(([label, paths]) => <div key={label}>
          <dt>{label}</dt><dd>{paths.length ? paths.map(folder => <code key={folder}>{folder}</code>) : <span className="cfg-help">{project.unclassifiedDirectories.length ? 'Awaiting runner update' : 'Not created'}</span>}</dd>
        </div>)}</dl>
        {project.unclassifiedDirectories.length > 0 ? <p className="cfg-help">This runner has not reported directory details yet.</p> : null}
        {canWrite && project.key.startsWith(UNREGISTERED) ? <div className="library-actions">
          {deleting === project.key
            ? <>
              <button className="btn btn-sm" disabled={pending} onClick={() => { setDeleting(null); void send({ kind: 'delete-work', key: project.key.slice(UNREGISTERED.length) }); }}>Confirm: delete this folder</button>
              <button className="btn btn-sm" onClick={() => setDeleting(null)}>Cancel</button>
            </>
            : <button className="btn btn-sm" disabled={pending} onClick={() => setDeleting(project.key)}>Delete folder</button>}
        </div> : null}
      </section>)}
    </details>
    {retired.length > 0 ? <details>
      <summary>Old copies from moved libraries ({retired.length})</summary>
      <p className="cfg-help">A project's previous copy is kept as a recovery backup when its library is removed with migration, and deleted automatically after 14 days.</p>
      <ul className="library-retired">{retired.map(copy => <li key={copy.path}>
        <code>{copy.path}</code>
        <p className="cfg-help">Moved {relativeTime(copy.retiredAt)}; deleted automatically on {new Date(copy.retiredAt + RETIRED_COPY_DAYS * 86_400_000).toLocaleDateString()}.</p>
        {canWrite ? <button className="btn btn-sm" disabled={pending} onClick={() => void send({ kind: 'delete-retired', path: copy.path })}>Delete now</button> : null}
      </li>)}</ul>
    </details> : null}
  </section>;
}
